//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode, SyntheticEvent, useState } from 'react';
import { IconButton, Menu } from '@mui/material';
import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';

type OverflowMenuProps = {
  children: ReactNode;
  disabled?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const OverflowMenu = ({ children, disabled = false, ...wrapperProps }: OverflowMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const [hasEntered, setHasEntered] = useState(false);

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: SyntheticEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleOnMouseEnter = () => {
    setHasEntered(true);
  };

  const handleOnMouseLeave = () => {
    if (hasEntered) {
      setTimeout(handleClose, 250);
    }
  };

  return (
    <Wrapper onClick={(e) => e.stopPropagation()} {...wrapperProps}>
      <OverflowIconButton
        aria-label="overflow-menu"
        aria-controls="overflow-menu"
        aria-haspopup="true"
        onClick={handleClick}
        disabled={disabled}
        size="large"
      >
        <PolarisIcon icon="overflow" height={16} />
      </OverflowIconButton>
      <Menu
        id="overflow-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        closeAfterTransition
      >
        <div onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave} onBlur={handleOnMouseLeave}>
          {children}
        </div>
      </Menu>
    </Wrapper>
  );
};

export default OverflowMenu;

const Wrapper = styled.div`
  text-align: right;
`;

const OverflowIconButton = styled(IconButton)`
  opacity: 0.4;
  padding: 5px;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
    transition: opacity 0.2s ease-in-out;
  }
`;
